import React, { useState } from 'react';
import { 
  Sparkles, 
  Layers, 
  PlusCircle, 
  ArrowRight, 
  X, 
  ShieldCheck, 
  RefreshCw, 
  History, 
  Syringe,
  CheckCircle2
} from 'lucide-react';

export default function OnboardingModal({
  isOpen,
  onClose,
  onLoadSampleTemplate,
  onStartBlank,
  isSeeding = false
}) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md animate-fadeIn">
      <div 
        className="relative w-full max-w-2xl bg-slate-900/95 border border-slate-800 rounded-3xl shadow-2xl p-6 sm:p-8 overflow-hidden text-slate-100"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Ambient background glows */}
        <div className="absolute top-0 right-0 -mr-20 -mt-20 w-64 h-64 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 -ml-20 -mb-20 w-64 h-64 bg-sky-500/10 rounded-full blur-3xl pointer-events-none" />

        {/* Dismiss Button */}
        <button
          onClick={onClose}
          disabled={isSeeding}
          className="absolute top-5 right-5 p-2 rounded-xl text-slate-400 hover:text-white hover:bg-slate-800/80 transition disabled:opacity-50 z-10"
          aria-label="Close welcome modal"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Modal Header */}
        <div className="relative z-10 mb-6 text-center sm:text-left">
          <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-semibold uppercase tracking-wider mb-3">
            <Sparkles className="w-3.5 h-3.5 animate-pulse" />
            <span>Welcome to Supps & Peps</span>
          </div>
          <h2 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
            Initialize Your Protocol Stack
          </h2>
          <p className="text-sm text-slate-400 mt-1.5 leading-relaxed max-w-xl">
            Track and optimize your supplement, peptide, and hormone / AAS regimens with complete data isolation, real-time sync, and automated audit logs.
          </p>
        </div>

        {/* Choice Grid */}
        <div className="relative z-10 grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          
          {/* Option 1: Load Sample Template */}
          <div className="flex flex-col justify-between p-5 rounded-2xl bg-gradient-to-b from-slate-800/90 to-slate-900/90 border-2 border-emerald-500/40 hover:border-emerald-500 transition-all shadow-lg hover:shadow-emerald-500/10 group">
            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="p-3 rounded-2xl bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 group-hover:scale-105 transition-transform">
                  <Sparkles className="w-6 h-6" />
                </div>
                <span className="text-[11px] font-bold uppercase tracking-wider px-2.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                  Recommended
                </span>
              </div>
              <h3 className="text-lg font-bold text-white mb-1.5">
                Load Sample Template
              </h3>
              <p className="text-xs text-slate-300 leading-relaxed">
                Pre-load a comprehensive stack of <strong className="text-white">14 research-backed compounds</strong> across Supplements (D3+K2, NMN, TMG, Astaxanthin), Peptides (Tirzepatide, Cagrilintide, GHK-Cu, CJC/Ipam), and Hormones & AAS (TRT, DHEA) with calibrated dosages.
              </p>
            </div>

            <button
              type="button"
              onClick={onLoadSampleTemplate}
              disabled={isSeeding}
              className="mt-5 w-full flex items-center justify-center space-x-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-400 hover:to-teal-500 text-slate-950 font-bold text-xs shadow-lg shadow-emerald-500/20 transition-all disabled:opacity-50"
            >
              <span>{isSeeding ? 'Loading Template...' : 'Load Sample Template'}</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          {/* Option 2: Start with Blank Stack */}
          <div className="flex flex-col justify-between p-5 rounded-2xl bg-slate-800/40 hover:bg-slate-800/60 border border-slate-700/80 hover:border-slate-600 transition-all group">
            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="p-3 rounded-2xl bg-sky-500/10 text-sky-400 border border-sky-500/20 group-hover:scale-105 transition-transform">
                  <PlusCircle className="w-6 h-6" />
                </div>
                <span className="text-[11px] font-semibold uppercase tracking-wider px-2.5 py-0.5 rounded-full bg-slate-800 text-slate-400 border border-slate-700">
                  Clean Slate
                </span>
              </div>
              <h3 className="text-lg font-bold text-white mb-1.5">
                Start with Blank Stack
              </h3>
              <p className="text-xs text-slate-400 leading-relaxed">
                Start with an empty dashboard and manually formulate your custom protocols, administration schedules, targets, and notes one compound at a time.
              </p>
            </div>

            <button
              type="button"
              onClick={onStartBlank}
              disabled={isSeeding}
              className="mt-5 w-full flex items-center justify-center space-x-2 px-4 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 hover:text-white font-semibold text-xs border border-slate-700 hover:border-slate-600 transition-all disabled:opacity-50"
            >
              <span>Start Blank Stack</span>
            </button>
          </div>

        </div>

        {/* Feature Highlights Footer */}
        <div className="relative z-10 pt-4 border-t border-slate-800/80 grid grid-cols-2 sm:grid-cols-4 gap-3 text-[11px] text-slate-400">
          <div className="flex items-center space-x-2">
            <ShieldCheck className="w-4 h-4 text-emerald-400 flex-shrink-0" />
            <span>Private User Isolation</span>
          </div>
          <div className="flex items-center space-x-2">
            <RefreshCw className="w-4 h-4 text-sky-400 flex-shrink-0" />
            <span>Live Multi-Device Sync</span>
          </div>
          <div className="flex items-center space-x-2">
            <History className="w-4 h-4 text-purple-400 flex-shrink-0" />
            <span>Audit Logs & Recovery</span>
          </div>
          <div className="flex items-center space-x-2">
            <Syringe className="w-4 h-4 text-rose-400 flex-shrink-0" />
            <span>Syringe Lab Engine</span>
          </div>
        </div>

      </div>
    </div>
  );
}
